import { cn } from "@msuite/ui/utils";
import { type PointerEvent as ReactPointerEvent, useRef } from "react";
import type { DayShiftRange } from "./capacity-planner-bar";

/** Props Interface */
interface CapacityPlannerBarHandleProps {
	/** Which end of the span this handle is, and therefore which edge of the box it sits against. */
	edge: "start" | "end";
	/**
	 * What the handle draws. `dot` is the work's own grip; `limiter` is the mark already standing at
	 * the end of the window, which becomes draggable rather than growing a second grip beside it.
	 */
	variant?: "dot" | "limiter";
	/** How far it may travel, in days. It stops dead at these rather than previewing a refusal. */
	range: DayShiftRange;
	/** The width of one day column, measured when the drag starts rather than while rendering. */
	getDayWidth: () => number;
	/** Called on every step while dragging, so the bar can show where the edge would land. */
	onPreview: (deltaDays: number) => void;
	/** Called once on release, with the distance travelled. Zero is reported too, so the preview ends. */
	onCommit: (deltaDays: number) => void;
}

/**
 * One draggable edge of a span drawn on the board — a ticket's work, or the window around it.
 *
 * It reports whole days and nothing else — what an edge moving means is the row's to decide, the
 * same bargain the bar itself makes about being dragged. The pointer is captured on the handle, so
 * a drag that leaves the row keeps going instead of stopping at the boundary.
 *
 * The pointer events stop here: the block underneath listens for a drag of the whole bar, and a
 * grab of the edge would otherwise start both at once.
 */
export function CapacityPlannerBarHandle({
	edge,
	variant = "dot",
	range,
	getDayWidth,
	onPreview,
	onCommit,
}: CapacityPlannerBarHandleProps) {
	/** Hooks */
	const originRef = useRef<number | null>(null);
	const dayWidthRef = useRef(0);
	// What the release reports. A pointerup batched with the last pointermove would otherwise commit
	// the previous frame's distance.
	const deltaRef = useRef(0);

	/** Functions */
	function handleOnPointerDown(event: ReactPointerEvent<HTMLDivElement>) {
		event.preventDefault();
		event.stopPropagation();
		dayWidthRef.current = getDayWidth();
		originRef.current = event.clientX;
		deltaRef.current = 0;
		event.currentTarget.setPointerCapture(event.pointerId);
	}

	function handleOnPointerMove(event: ReactPointerEvent<HTMLDivElement>) {
		if (originRef.current === null || !dayWidthRef.current) return;
		event.stopPropagation();
		const travelled = Math.round((event.clientX - originRef.current) / dayWidthRef.current);
		const clamped = Math.min(range.max, Math.max(range.min, travelled));
		if (clamped === deltaRef.current) return;
		deltaRef.current = clamped;
		onPreview(clamped);
	}

	function handleOnPointerUp(event: ReactPointerEvent<HTMLDivElement>) {
		if (originRef.current === null) return;
		event.stopPropagation();
		originRef.current = null;
		event.currentTarget.releasePointerCapture(event.pointerId);

		const travelled = deltaRef.current;
		deltaRef.current = 0;
		onCommit(travelled);
	}

	/** Render */
	// The grab area is wider than the dot it draws, because an edge a few pixels tall is not
	// something a pointer lands on reliably.
	//
	// It sits **inside** the work rather than centred on its edge. Centred reads better, but the
	// half that would hang past the last column is scrollable overflow, and the board's scroll
	// region answers it with a horizontal scrollbar across the whole calendar.
	return (
		<div
			className={cn(
				"absolute inset-y-0 flex items-center px-1 pointer-events-auto cursor-ew-resize touch-none select-none",
				edge === "start" ? "left-0" : "right-0",
			)}
			data-edge={edge}
			onPointerDown={handleOnPointerDown}
			onPointerMove={handleOnPointerMove}
			onPointerUp={handleOnPointerUp}
			onPointerCancel={handleOnPointerUp}
		>
			{variant === "dot" ? (
				<div className="size-2 rounded-full bg-yellow-500 border-[1px] border-white shadow-sm" />
			) : (
				// Flush with the edge it marks rather than centred in the grab area, which is padded
				// inwards: a limiter standing four pixels short of the rail's end reads as a mistake,
				// and it is the end of the window it has to name.
				<div
					className={cn(
						"absolute top-1/2 -translate-y-1/2 h-1/2 w-[2px] rounded-full bg-gray-400",
						edge === "start" ? "left-0" : "right-0",
					)}
				/>
			)}
		</div>
	);
}
